import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../api';

export default function TrainingPage() {
  const { t } = useTranslation();
  const { presenceId } = useParams();
  
  const [activeTab, setActiveTab] = useState('qa');
  const [qaExamples, setQaExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Q&A Form state
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  
  // Conversation Review state
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [correctedAnswer, setCorrectedAnswer] = useState('');
  
  // Bulk Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Metrics state
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  
  // Auto-Learning Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [autoLearningStats, setAutoLearningStats] = useState(null);
  
  // Social Media Import state
  const [socialMediaFile, setSocialMediaFile] = useState(null);
  const [socialMediaPlatform, setSocialMediaPlatform] = useState('instagram');
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [supportedPlatforms, setSupportedPlatforms] = useState([]);
  
  // Chat History Import state
  const [chatFile, setChatFile] = useState(null);
  const [chatPlatform, setChatPlatform] = useState('whatsapp');
  const [chatImporting, setChatImporting] = useState(false);
  const [chatProgress, setChatProgress] = useState(0);
  const [chatResult, setChatResult] = useState(null);
  const [chatPlatforms, setChatPlatforms] = useState([]);
  
  // Voice Note state
  const [voiceFile, setVoiceFile] = useState(null);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [voiceProgress, setVoiceProgress] = useState(0);
  const [voiceResult, setVoiceResult] = useState(null);
  const [voiceLanguage, setVoiceLanguage] = useState('tr');
  
  useEffect(() => {
    loadTrainingData();
  }, [presenceId]);
  
  useEffect(() => {
    if (activeTab === 'review') {
      loadPendingReviews();
    } else if (activeTab === 'metrics') {
      loadMetrics();
    } else if (activeTab === 'suggestions') {
      loadSuggestions();
    } else if (activeTab === 'social-media') {
      loadSupportedPlatforms();
    } else if (activeTab === 'chat-history') {
      loadChatPlatforms();
    } else if (activeTab === 'voice-notes') {
      // No initial loading needed
    }
  }, [activeTab, presenceId]);
  
  const loadTrainingData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/training/qa/${presenceId}`);
      setQaExamples(res.data.map(ex => ({
        id: ex._id,
        question: ex.question,
        answer: ex.answer,
        category: ex.category,
        createdAt: ex.created_at
      })));
    } catch (err) {
      console.error(err);
      toast.error(t('training.loadError'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddQA = async (e) => {
    e.preventDefault();
    
    if (!newQuestion.trim() || !newAnswer.trim()) {
      toast.error(t('training.fillAllFields'));
      return;
    }
    
    try {
      const res = await api.post(`/training/qa/${presenceId}`, {
        question: newQuestion,
        answer: newAnswer,
        category: newCategory
      });
      
      // Add to local state
      const newExample = {
        id: res.data._id,
        question: res.data.question,
        answer: res.data.answer,
        category: res.data.category,
        createdAt: res.data.created_at
      };
      
      setQaExamples([newExample, ...qaExamples]);
      setNewQuestion('');
      setNewAnswer('');
      setNewCategory('general');
      
      toast.success(t('training.qaAdded'));
    } catch (err) {
      console.error(err);
      toast.error(t('training.addError'));
    }
  };
  
  const handleDeleteQA = async (id) => {
    if (!window.confirm(t('training.confirmDelete'))) return;
    
    try {
      await api.delete(`/training/qa/${presenceId}/${id}`);
      
      setQaExamples(qaExamples.filter(ex => ex.id !== id));
      toast.success(t('training.qaDeleted'));
    } catch (err) {
      console.error(err);
      toast.error(t('training.deleteError'));
    }
  };
  
  // ===== Conversation Review Functions =====
  
  const loadPendingReviews = async () => {
    setReviewsLoading(true);
    try {
      const res = await api.get(`/training/reviews/${presenceId}`);
      setPendingReviews(res.data);
    } catch (err) {
      console.error(err);
      toast.error(t('training.reviewsLoadError'));
    } finally {
      setReviewsLoading(false);
    }
  };
  
  const handleRating = async (review, rating) => {
    try {
      await api.post('/training/feedback', {
        presenceId: presenceId,
        conversationId: review.conversation_id,
        messageId: review.message_id,
        rating: rating
      });
      
      // Remove from pending list
      setPendingReviews(pendingReviews.filter(r => r.message_id !== review.message_id));
      toast.success(t('training.feedbackSaved'));
    } catch (err) {
      console.error(err);
      toast.error(t('training.feedbackError'));
    }
  };
  
  const handleCorrectAnswer = async (review) => {
    if (!correctedAnswer.trim()) {
      toast.error(t('training.fillCorrectedAnswer'));
      return;
    }
    
    try {
      await api.post('/training/feedback', {
        presenceId: presenceId,
        conversationId: review.conversation_id,
        messageId: review.message_id,
        rating: 'thumbs_down',
        correctedAnswer: correctedAnswer
      });
      
      setPendingReviews(pendingReviews.filter(r => r.message_id !== review.message_id));
      setSelectedReview(null);
      setCorrectedAnswer('');
      toast.success(t('training.answerCorrected'));
      
      // Reload Q&A to show new example
      loadTrainingData();
    } catch (err) {
      console.error(err);
      toast.error(t('training.correctionError'));
    }
  };
  
  // ===== Bulk Upload Functions =====
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        let parsed = [];
        
        if (file.name.endsWith('.json')) {
          parsed = JSON.parse(content);
        } else if (file.name.endsWith('.csv')) {
          // Simple CSV parsing
          const lines = content.split('\n');
          const headers = lines[0].split(',').map(h => h.trim());
          
          for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue;
            const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            const obj = {};
            headers.forEach((h, idx) => {
              obj[h] = values[idx];
            });
            parsed.push(obj);
          }
        }
        
        setUploadPreview(parsed.slice(0, 10)); // Show first 10
      } catch (err) {
        toast.error(t('training.fileParseError'));
        console.error(err);
      }
    };
    
    reader.readAsText(file);
  };
  
  const handleBulkUpload = async () => {
    if (!uploadFile || uploadPreview.length === 0) {
      toast.error(t('training.noFileSelected'));
      return;
    }
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const content = event.target.result;
          let examples = [];
          
          if (uploadFile.name.endsWith('.json')) {
            examples = JSON.parse(content);
          } else if (uploadFile.name.endsWith('.csv')) {
            const lines = content.split('\n');
            const headers = lines[0].split(',').map(h => h.trim());
            
            for (let i = 1; i < lines.length; i++) {
              if (!lines[i].trim()) continue;
              const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
              const obj = {};
              headers.forEach((h, idx) => {
                obj[h] = values[idx];
              });
              examples.push(obj);
            }
          }
          
          const res = await api.post(`/training/bulk/${presenceId}`, examples);
          
          toast.success(`${res.data.inserted_count} ${t('training.examplesUploaded')}`);
          setUploadFile(null);
          setUploadPreview([]);
          
          // Reload Q&A
          loadTrainingData();
        } catch (err) {
          console.error(err);
          toast.error(t('training.uploadError'));
        } finally {
          setUploading(false);
        }
      };
      
      reader.readAsText(uploadFile);
    } catch (err) {
      console.error(err);
      toast.error(t('training.uploadError'));
      setUploading(false);
    }
  };
  
  // ===== Metrics Functions =====
  
  const loadMetrics = async () => {
    setMetricsLoading(true);
    try {
      const res = await api.get(`/training/metrics/${presenceId}`);
      setMetrics(res.data);
    } catch (err) {
      console.error(err);
      toast.error(t('training.metricsLoadError'));
    } finally {
      setMetricsLoading(false);
    }
  };

  // ===== Auto-Learning Suggestions Functions =====
  
  const loadSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const [suggestionsRes, statsRes] = await Promise.all([
        api.get(`/auto-learning/suggestions/${presenceId}`),
        api.get(`/auto-learning/stats/${presenceId}`)
      ]);
      
      setSuggestions(suggestionsRes.data.suggestions || []);
      setAutoLearningStats(statsRes.data);
    } catch (err) {
      console.error(err);
      toast.error(t('training.loadError'));
    } finally {
      setSuggestionsLoading(false);
    }
  };
  
  const handleApproveSuggestion = async (suggestionId) => {
    try {
      await api.post('/auto-learning/approve', {
        suggestionId,
        presenceId
      });
      
      toast.success(t('training.suggestionApproved'));
      
      // Reload suggestions and Q&A examples
      loadSuggestions();
      loadTrainingData();
    } catch (err) {
      console.error(err);
      toast.error(t('training.approveError'));
    }
  };
  
  const handleRejectSuggestion = async (suggestionId) => {
    try {
      await api.post('/auto-learning/reject', {
        suggestionId,
        presenceId
      });
      
      toast.success(t('training.suggestionRejected'));
      
      // Reload suggestions
      loadSuggestions();
    } catch (err) {
      console.error(err);
      toast.error(t('training.rejectError'));
    }
  };

  // Social Media Import Functions
  const loadSupportedPlatforms = async () => {
    try {
      const res = await api.get('/social-media/supported-platforms');
      setSupportedPlatforms(res.data.platforms || []);
    } catch (err) {
      console.error('Error loading platforms:', err);
      // Set default platforms if API fails
      setSupportedPlatforms([
        {
          id: 'instagram',
          name: 'Instagram',
          description: 'Gönderiler, yorumlar ve hikaye arşivinizi analiz eder',
          fileFormats: ['JSON', 'ZIP']
        },
        {
          id: 'twitter',
          name: 'Twitter / X',
          description: 'Tweetlerinizi ve etkileşimlerinizi analiz eder',
          fileFormats: ['JSON', 'ZIP']
        },
        {
          id: 'facebook',
          name: 'Facebook',
          description: 'Gönderilerinizi, yorumlarınızı ve fotoğraflarınızı analiz eder',
          fileFormats: ['JSON', 'ZIP']
        }
      ]);
    }
  };

  const handleSocialMediaFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Dosya boyutu 50MB\'dan büyük olamaz');
      return;
    }
    
    // Check file type
    const validExtensions = ['.json', '.zip'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Sadece JSON veya ZIP dosyaları yükleyebilirsiniz');
      return;
    }
    
    setSocialMediaFile(file);
    setImportResult(null);
  };

  const handleSocialMediaImport = async () => {
    if (!socialMediaFile) {
      toast.error('Lütfen bir dosya seçin');
      return;
    }
    
    setImporting(true);
    setImportProgress(10);
    
    try {
      const formData = new FormData();
      formData.append('file', socialMediaFile);
      formData.append('platform', socialMediaPlatform);
      
      setImportProgress(30);
      
      const res = await api.post(`/social-media/import/${presenceId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            30 + (progressEvent.loaded * 40) / progressEvent.total
          );
          setImportProgress(percentCompleted);
        }
      });
      
      setImportProgress(90);
      
      if (res.data.success) {
        setImportResult(res.data);
        setImportProgress(100);
        toast.success(`${res.data.episodicMemories + res.data.semanticMemories} hafıza ve ${res.data.qaExamples} Q&A oluşturuldu!`);
        
        // Reload training data to show new Q&As
        loadTrainingData();
      } else {
        toast.error(res.data.error || 'İçe aktarma başarısız');
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || 'İçe aktarma sırasında hata oluştu');
    } finally {
      setImporting(false);
      setTimeout(() => setImportProgress(0), 2000);
    }
  };

  const handleClearImport = () => {
    setSocialMediaFile(null);
    setImportResult(null);
    setImportProgress(0);
  };

  // Chat History Import Functions
  const loadChatPlatforms = async () => {
    try {
      const res = await api.get('/chat-history/supported-platforms');
      setChatPlatforms(res.data.platforms || []);
    } catch (err) {
      console.error('Error loading chat platforms:', err);
      setChatPlatforms([
        {
          id: 'whatsapp',
          name: 'WhatsApp',
          description: 'Konuşma tarzınızı, emoji kullanımınızı öğrenir',
          fileFormats: ['TXT']
        },
        {
          id: 'telegram',
          name: 'Telegram',
          description: 'İletişim kalıplarınızı analiz eder',
          fileFormats: ['JSON']
        }
      ]);
    }
  };

  const handleChatFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (100MB for chat files)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Dosya boyutu 100MB\'dan büyük olamaz');
      return;
    }
    
    // Check file type based on platform
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (chatPlatform === 'whatsapp' && fileExtension !== '.txt') {
      toast.error('WhatsApp için sadece .txt dosyası yükleyebilirsiniz');
      return;
    }
    
    if (chatPlatform === 'telegram' && !['.json', '.zip'].includes(fileExtension)) {
      toast.error('Telegram için .json veya .zip dosyası yükleyebilirsiniz');
      return;
    }
    
    setChatFile(file);
    setChatResult(null);
  };

  const handleChatImport = async () => {
    if (!chatFile) {
      toast.error('Lütfen bir dosya seçin');
      return;
    }
    
    setChatImporting(true);
    setChatProgress(10);
    
    try {
      const formData = new FormData();
      formData.append('file', chatFile);
      formData.append('platform', chatPlatform);
      
      setChatProgress(30);
      
      const res = await api.post(`/chat-history/import/${presenceId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            30 + (progressEvent.loaded * 50) / progressEvent.total
          );
          setChatProgress(percentCompleted);
        }
      });
      
      setChatProgress(90);
      
      if (res.data.success) {
        setChatResult(res.data);
        setChatProgress(100);
        toast.success(`${res.data.totalMessages} mesaj analiz edildi! ${res.data.memoriesCreated} hafıza ve ${res.data.qaExamplesCreated} Q&A oluşturuldu.`);
        
        // Reload training data
        loadTrainingData();
      } else {
        toast.error(res.data.error || 'İçe aktarma başarısız');
      }
    } catch (err) {
      console.error('Chat import error:', err);
      toast.error(err.response?.data?.detail || 'İçe aktarma sırasında hata oluştu');
    } finally {
      setChatImporting(false);
      setTimeout(() => setChatProgress(0), 2000);
    }
  };

  const handleClearChatImport = () => {
    setChatFile(null);
    setChatResult(null);
    setChatProgress(0);
  };

  // Voice Note Functions
  const handleVoiceFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (25MB for audio)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Dosya boyutu 25MB\'dan büyük olamaz');
      return;
    }
    
    // Check file type
    const validExtensions = ['.mp3', '.wav', '.m4a', '.webm', '.ogg'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Sadece MP3, WAV, M4A, WebM, OGG dosyaları desteklenir');
      return;
    }
    
    setVoiceFile(file);
    setVoiceResult(null);
  };

  const handleVoiceProcess = async () => {
    if (!voiceFile) {
      toast.error('Lütfen bir ses dosyası seçin');
      return;
    }
    
    setVoiceProcessing(true);
    setVoiceProgress(10);
    
    try {
      const formData = new FormData();
      formData.append('file', voiceFile);
      formData.append('language', voiceLanguage);
      
      setVoiceProgress(30);
      
      const res = await api.post(`/voice-notes/process/${presenceId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            30 + (progressEvent.loaded * 50) / progressEvent.total
          );
          setVoiceProgress(percentCompleted);
        }
      });
      
      setVoiceProgress(90);
      
      if (res.data.success) {
        setVoiceResult(res.data);
        setVoiceProgress(100);
        toast.success(`Sesli not işlendi! ${res.data.memoriesCreated} hafıza ve ${res.data.qaCreated} Q&A oluşturuldu.`);
        
        // Reload training data
        loadTrainingData();
      } else {
        toast.error(res.data.error || 'İşleme başarısız');
      }
    } catch (err) {
      console.error('Voice processing error:', err);
      toast.error(err.response?.data?.detail || 'İşleme sırasında hata oluştu');
    } finally {
      setVoiceProcessing(false);
      setTimeout(() => setVoiceProgress(0), 2000);
    }
  };

  const handleClearVoice = () => {
    setVoiceFile(null);
    setVoiceResult(null);
    setVoiceProgress(0);
  };



  return (
    <div className="max-w-6xl mx-auto p-4 lg:p-6">
      {/* Header */}
      <header className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-2">
          {t('training.title')}
        </h1>
        <p className="text-sm lg:text-base text-slate-600 dark:text-slate-400">
          {t('training.description')}
        </p>
      </header>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700 mb-4 lg:mb-6 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 lg:space-x-8 min-w-max">
          <button
            onClick={() => setActiveTab('qa')}
            className={`py-3 lg:py-4 px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
              activeTab === 'qa'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
            }`}
          >
            {t('training.qaExamples')}
          </button>
          <button
            onClick={() => setActiveTab('review')}
            className={`py-3 lg:py-4 px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
              activeTab === 'review'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
            }`}
          >
            {t('training.conversationReview')}
          </button>
          <button
            onClick={() => setActiveTab('suggestions')}
            className={`py-3 lg:py-4 px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
              activeTab === 'suggestions'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
            }`}
          >
            🤖 {t('training.suggestions')}
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            className={`py-3 lg:py-4 px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
              activeTab === 'bulk'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
            }`}
          >
            {t('training.bulkUpload')}
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={`py-3 lg:py-4 px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
              activeTab === 'metrics'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
            }`}
          >
            {t('training.metrics')}
          </button>
          <button
            onClick={() => setActiveTab('social-media')}
            className={`py-3 lg:py-4 px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
              activeTab === 'social-media'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
            }`}
          >
            📱 Sosyal Medya
          </button>
          <button
            onClick={() => setActiveTab('chat-history')}
            className={`py-3 lg:py-4 px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
              activeTab === 'chat-history'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
            }`}
          >
            💬 Chat
          </button>
          <button
            onClick={() => setActiveTab('voice-notes')}
            className={`py-3 lg:py-4 px-1 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
              activeTab === 'voice-notes'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
            }`}
          >
            🎤 Sesli Not
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'qa' && (
        <div className="space-y-4 lg:space-y-6">
          {/* Add Q&A Form */}
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 lg:p-6">
            <h3 className="text-base lg:text-lg font-semibold text-slate-900 dark:text-slate-50 mb-3 lg:mb-4">
              {t('training.addNewQA')}
            </h3>
            <form onSubmit={handleAddQA} className="space-y-3 lg:space-y-4">
              <div>
                <label className="block text-xs lg:text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('training.question')}
                </label>
                <input
                  type="text"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  className="w-full px-3 lg:px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50"
                  placeholder={t('training.questionPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-xs lg:text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('training.answer')}
                </label>
                <textarea
                  value={newAnswer}
                  onChange={(e) => setNewAnswer(e.target.value)}
                  rows={3}
                  className="w-full px-3 lg:px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50"
                  placeholder={t('training.answerPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-xs lg:text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('training.category')}
                </label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full px-3 lg:px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50"
                >
                  <option value="general">{t('training.categoryGeneral')}</option>
                  <option value="personal">{t('training.categoryPersonal')}</option>
                  <option value="professional">{t('training.categoryProfessional')}</option>
                  <option value="family">{t('training.categoryFamily')}</option>
                  <option value="hobbies">{t('training.categoryHobbies')}</option>
                </select>
              </div>
              
              <button
                type="submit"
                className="w-full bg-cyan-600 text-white py-2 px-4 rounded-lg hover:bg-cyan-700 transition-colors font-medium text-sm lg:text-base"
              >
                {t('training.addExample')}
              </button>
            </form>
          </div>

          {/* Q&A List */}
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="p-4 lg:p-6 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-base lg:text-lg font-semibold text-slate-900 dark:text-slate-50">
                {t('training.existingExamples')} ({qaExamples.length})
              </h3>
            </div>
            
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {loading ? (
                <div className="p-6 lg:p-8 text-center text-slate-500 text-sm">
                  {t('training.loading')}
                </div>
              ) : qaExamples.length === 0 ? (
                <div className="p-6 lg:p-8 text-center text-slate-500 text-sm">
                  {t('training.noExamples')}
                </div>
              ) : (
                qaExamples.map((example) => (
                  <div key={example.id} className="p-4 lg:p-6 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {t(`training.category${example.category.charAt(0).toUpperCase() + example.category.slice(1)}`)}
                      </span>
                      <button
                        onClick={() => handleDeleteQA(example.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        {t('training.delete')}
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs font-semibold text-slate-500 uppercase">
                          {t('training.question')}
                        </span>
                        <p className="text-slate-900 font-medium mt-1">
                          {example.question}
                        </p>
                      </div>
                      
                      <div>
                        <span className="text-xs font-semibold text-slate-500 uppercase">
                          {t('training.answer')}
                        </span>
                        <p className="text-slate-700 mt-1">
                          {example.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'review' && (
        <div className="space-y-4 lg:space-y-6">
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="p-4 lg:p-6 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-base lg:text-lg font-semibold text-slate-900 dark:text-slate-50">
                {t('training.pendingReviews')} ({pendingReviews.length})
              </h3>
              <p className="text-xs lg:text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t('training.reviewHelp')}
              </p>
            </div>
            
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {reviewsLoading ? (
                <div className="p-6 lg:p-8 text-center text-slate-500 text-sm">
                  {t('training.loading')}
                </div>
              ) : pendingReviews.length === 0 ? (
                <div className="p-6 lg:p-8 text-center text-slate-500 text-sm">
                  <div className="text-4xl mb-3">✅</div>
                  <p>{t('training.noReviews')}</p>
                  <p className="text-xs text-slate-400 mt-2">{t('training.noReviewsDesc')}</p>
                </div>
              ) : (
                pendingReviews.map((review) => (
                  <div key={review.message_id} className="p-4 lg:p-6">
                    <div className="space-y-3 lg:space-y-4">
                      {/* User Question */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 lg:p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">
                          👤 {t('training.userQuestion')}
                        </div>
                        <p className="text-sm text-slate-900 dark:text-slate-100">{review.user_question}</p>
                      </div>
                      
                      {/* Bot Answer */}
                      <div className="bg-slate-50 dark:bg-slate-800/50 p-3 lg:p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">
                          🤖 {t('training.botAnswer')}
                        </div>
                        <p className="text-sm text-slate-900 dark:text-slate-100">{review.bot_answer}</p>
                      </div>
                      
                      {/* Rating Buttons */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 lg:gap-4">
                        <button
                          onClick={() => handleRating(review, 'thumbs_up')}
                          className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                        >
                          <span>👍</span>
                          <span>{t('training.goodAnswer')}</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            setSelectedReview(review);
                            setCorrectedAnswer(review.bot_answer);
                          }}
                          className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                        >
                          <span>👎</span>
                          <span>{t('training.correctAnswer')}</span>
                        </button>
                      </div>
                      
                      {/* Correction Form */}
                      {selectedReview?.message_id === review.message_id && (
                        <div className="mt-3 lg:mt-4 p-3 lg:p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800 rounded-lg">
                          <label className="block text-xs lg:text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            ✏️ {t('training.correctedAnswer')}
                          </label>
                          <textarea
                            value={correctedAnswer}
                            onChange={(e) => setCorrectedAnswer(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent mb-3"
                            placeholder={t('training.enterCorrectedAnswer')}
                          />
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              onClick={() => handleCorrectAnswer(review)}
                              className="px-4 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                            >
                              {t('training.saveCorrection')}
                            </button>
                            <button
                              onClick={() => {
                                setSelectedReview(null);
                                setCorrectedAnswer('');
                              }}
                              className="px-4 py-2 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'bulk' && (
        <div className="space-y-4 lg:space-y-6">
          {/* Upload Section */}
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 lg:p-6">
            <h3 className="text-base lg:text-lg font-semibold text-slate-900 dark:text-slate-50 mb-3 lg:mb-4">
              📤 {t('training.bulkUpload')}
            </h3>
            
            <div className="space-y-3 lg:space-y-4">
              <div>
                <label className="block text-xs lg:text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('training.selectFile')}
                </label>
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-slate-500 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs lg:file:text-sm file:font-semibold file:bg-cyan-50 dark:file:bg-cyan-900/30 file:text-cyan-700 dark:file:text-cyan-400 hover:file:bg-cyan-100 dark:hover:file:bg-cyan-900/50 cursor-pointer"
                />
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {t('training.fileFormats')}
                </p>
              </div>
              
              {/* Format Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 lg:p-4 rounded-lg">
                <h4 className="text-xs lg:text-sm font-semibold text-blue-900 dark:text-blue-400 mb-2">
                  📋 {t('training.expectedFormat')}
                </h4>
                <div className="text-xs text-blue-800 dark:text-blue-300 space-y-1 font-mono">
                  <p><strong>CSV:</strong> question,answer,category</p>
                  <p><strong>JSON:</strong> [{"{"}"question":"...","answer":"...","category":"..."{"}"}]</p>
                </div>
              </div>
              
              {uploadFile && (
                <button
                  onClick={handleBulkUpload}
                  disabled={uploading}
                  className="w-full bg-cyan-600 text-white py-2 px-4 text-sm rounded-lg hover:bg-cyan-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? t('training.uploading') : t('training.uploadFile')}
                </button>
              )}
            </div>
          </div>
          
          {/* Preview */}
          {uploadPreview.length > 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="p-4 lg:p-6 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-base lg:text-lg font-semibold text-slate-900 dark:text-slate-50">
                  👁️ {t('training.preview')} ({uploadPreview.length} {t('training.ofTotal')})
                </h3>
              </div>
              
              <div className="divide-y divide-slate-200 dark:divide-slate-700 max-h-96 overflow-y-auto">
                {uploadPreview.map((item, idx) => (
                  <div key={idx} className="p-3 lg:p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <div className="flex items-start gap-3 lg:gap-4">
                      <div className="flex-shrink-0 w-6 h-6 lg:w-8 lg:h-8 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 rounded-full flex items-center justify-center text-xs lg:text-sm font-semibold">
                        {idx + 1}
                      </div>
                      <div className="flex-1 space-y-2 min-w-0">
                        <div>
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Q:</span>
                          <p className="text-xs lg:text-sm text-slate-900 dark:text-slate-100 break-words">{item.question}</p>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">A:</span>
                          <p className="text-xs lg:text-sm text-slate-700 dark:text-slate-300 break-words">{item.answer}</p>
                        </div>
                        {item.category && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600">
                            {item.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'metrics' && (
        <div className="space-y-6">
          {metricsLoading ? (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
              <div className="text-slate-500">{t('training.loading')}</div>
            </div>
          ) : metrics ? (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
                <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 lg:p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs lg:text-sm font-medium text-slate-600 dark:text-slate-400">{t('training.totalQA')}</h4>
                    <span className="text-xl lg:text-2xl">📚</span>
                  </div>
                  <p className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-50">{metrics.qa_examples?.total || 0}</p>
                </div>
                
                <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 lg:p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs lg:text-sm font-medium text-slate-600 dark:text-slate-400">{t('training.feedbackCount')}</h4>
                    <span className="text-xl lg:text-2xl">💬</span>
                  </div>
                  <p className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-50">{metrics.feedback_count || 0}</p>
                </div>
                
                <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 lg:p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs lg:text-sm font-medium text-slate-600 dark:text-slate-400">{t('training.averageRating')}</h4>
                    <span className="text-xl lg:text-2xl">⭐</span>
                  </div>
                  <p className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-50">{metrics.average_rating || 0}/5</p>
                </div>
                
                <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 lg:p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs lg:text-sm font-medium text-slate-600 dark:text-slate-400">{t('training.convertedQA')}</h4>
                    <span className="text-xl lg:text-2xl">✅</span>
                  </div>
                  <p className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-50">{metrics.converted_to_qa || 0}</p>
                </div>
              </div>
              
              {/* Category Breakdown */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-6">
                  {t('training.categoryBreakdown')}
                </h3>
                
                <div className="space-y-4">
                  {Object.entries(metrics.qa_examples || {}).filter(([key]) => key !== 'total').map(([category, count]) => {
                    const percentage = metrics.qa_examples.total > 0 
                      ? (count / metrics.qa_examples.total * 100).toFixed(0)
                      : 0;
                    
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700 capitalize">
                            {t(`training.category${category.charAt(0).toUpperCase() + category.slice(1)}`)}
                          </span>
                          <span className="text-sm text-slate-600">
                            {count} ({percentage}%)
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Feedback Stats */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-6">
                  {t('training.feedbackStats')}
                </h3>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-4xl mb-2">👍</div>
                    <p className="text-2xl font-bold text-green-600">{metrics.thumbs_up || 0}</p>
                    <p className="text-sm text-slate-600">{t('training.thumbsUp')}</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-4xl mb-2">👎</div>
                    <p className="text-2xl font-bold text-red-600">{metrics.thumbs_down || 0}</p>
                    <p className="text-sm text-slate-600">{t('training.thumbsDown')}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
              <p className="text-slate-500">{t('training.noMetrics')}</p>
            </div>
          )}
        </div>
      )}
      
      {/* Suggestions Tab - Auto-Learning */}
      {activeTab === 'suggestions' && (
        <div className="space-y-4 lg:space-y-6">
          {/* Stats Cards */}
          {autoLearningStats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('training.totalGenerated')}</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">{autoLearningStats.totalSuggestions}</div>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('training.waitingReview')}</div>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{autoLearningStats.pendingSuggestions}</div>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('training.autoApproved')}</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{autoLearningStats.approvedSuggestions}</div>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('training.confidence')}</div>
                <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">{autoLearningStats.avgConfidence}/10</div>
              </div>
            </div>
          )}
          
          {/* Suggestions List */}
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="p-4 lg:p-6 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-base lg:text-lg font-semibold text-slate-900 dark:text-slate-50">
                🤖 {t('training.pendingSuggestions')} ({suggestions.length})
              </h3>
              <p className="text-xs lg:text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t('training.suggestionsDesc')}
              </p>
            </div>
            
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {suggestionsLoading ? (
                <div className="p-6 lg:p-8 text-center text-slate-500 text-sm">
                  {t('training.loading')}
                </div>
              ) : suggestions.length === 0 ? (
                <div className="p-6 lg:p-8 text-center">
                  <div className="text-4xl mb-3">🎓</div>
                  <p className="text-slate-500 text-sm">{t('training.noSuggestions')}</p>
                  <p className="text-xs text-slate-400 mt-2">{t('training.noSuggestionsDesc')}</p>
                </div>
              ) : (
                suggestions.map((suggestion) => (
                  <div key={suggestion.id} className="p-4 lg:p-6 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <div className="space-y-3">
                      {/* Question */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">
                          ❓ {t('training.question')}
                        </div>
                        <p className="text-sm text-slate-900 dark:text-slate-100">{suggestion.question}</p>
                      </div>
                      
                      {/* Answer */}
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="text-xs font-semibold text-green-600 dark:text-green-400 mb-1">
                          ✅ {t('training.answer')}
                        </div>
                        <p className="text-sm text-slate-900 dark:text-slate-100">{suggestion.answer}</p>
                      </div>
                      
                      {/* Metadata */}
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600">
                          📁 {suggestion.category}
                        </span>
                        <span className={`px-2 py-1 rounded-full font-medium ${
                          suggestion.confidence >= 8
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
                            : suggestion.confidence >= 6
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700'
                        }`}>
                          💯 {t('training.confidence')}: {suggestion.confidence}/10
                        </span>
                      </div>
                      
                      {/* Reason */}
                      {suggestion.reason && (
                        <div className="text-xs text-slate-600 dark:text-slate-400 italic">
                          💡 {t('training.reason')}: {suggestion.reason}
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <button
                          onClick={() => handleApproveSuggestion(suggestion.id)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          <span>✅</span>
                          {t('training.approveSuggestion')}
                        </button>
                        <button
                          onClick={() => handleRejectSuggestion(suggestion.id)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          <span>❌</span>
                          {t('training.rejectSuggestion')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Social Media Import Tab */}
      {activeTab === 'social-media' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-4 lg:p-6 border border-slate-200 dark:border-slate-700">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-slate-900 dark:text-white mb-2">
                📱 Sosyal Medya İçe Aktarma
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Sosyal medya verilerinizi içe aktararak dijital varlığınızı otomatik olarak öğretebilirsiniz. 
                Sistem GPT-4 kullanarak gönderilerinizi analiz eder ve hafızalar ile Q&A örnekleri oluşturur.
              </p>
            </div>

            {/* Platform Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Platform Seçin
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {supportedPlatforms.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => setSocialMediaPlatform(platform.id)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      socialMediaPlatform === platform.id
                        ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <div className="font-semibold text-slate-900 dark:text-white mb-1">
                      {platform.name}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      {platform.description}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                      {platform.fileFormats?.join(', ')}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Dosya Yükle
              </label>
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  socialMediaFile 
                    ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' 
                    : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'
                }`}
              >
                {socialMediaFile ? (
                  <div>
                    <div className="text-4xl mb-2">📄</div>
                    <div className="font-medium text-slate-900 dark:text-white mb-1">
                      {socialMediaFile.name}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      {(socialMediaFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    <button
                      onClick={handleClearImport}
                      className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Dosyayı Kaldır
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl mb-2">📁</div>
                    <div className="text-slate-600 dark:text-slate-400 mb-3">
                      JSON veya ZIP dosyanızı buraya sürükleyin
                    </div>
                    <label className="inline-block px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 cursor-pointer transition-colors">
                      Dosya Seç
                      <input
                        type="file"
                        accept=".json,.zip"
                        onChange={handleSocialMediaFileSelect}
                        className="hidden"
                      />
                    </label>
                    <div className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                      Maksimum dosya boyutu: 50MB
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            {importing && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mb-2">
                  <span>İşleniyor...</span>
                  <span>{importProgress}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Import Button */}
            <button
              onClick={handleSocialMediaImport}
              disabled={!socialMediaFile || importing}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                !socialMediaFile || importing
                  ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white hover:from-cyan-700 hover:to-purple-700'
              }`}
            >
              {importing ? 'İçe Aktarılıyor...' : '🚀 İçe Aktarmayı Başlat'}
            </button>

            {/* Import Results */}
            {importResult && importResult.success && (
              <div className="mt-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">✅</span>
                  <div className="flex-1">
                    <h4 className="font-bold text-green-800 dark:text-green-300 mb-3">
                      İçe Aktarma Başarılı!
                    </h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {importResult.episodicMemories}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Anı Hafıza
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                          {importResult.semanticMemories}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Bilgi Hafızası
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {importResult.qaExamples}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Q&A Örneği
                        </div>
                      </div>
                    </div>

                    {importResult.insights?.summary && (
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          📊 Analiz Özeti:
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          {importResult.insights.summary}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* How to Export Instructions */}
            <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-3">
                💡 Verilerinizi Nasıl Dışa Aktarırsınız?
              </h4>
              <div className="space-y-3 text-sm text-blue-800 dark:text-blue-400">
                {supportedPlatforms.map((platform) => (
                  <div key={platform.id}>
                    <strong>{platform.name}:</strong> {platform.exportInstructions || 'Ayarlar > Veri İndirme'}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat History Import Tab */}
      {activeTab === 'chat-history' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-4 lg:p-6 border border-slate-200 dark:border-slate-700">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-slate-900 dark:text-white mb-2">
                💬 Chat Geçmişi İçe Aktarma
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                WhatsApp veya Telegram chat geçmişinizi yükleyin. GPT-4 konuşma tarzınızı, emoji kullanımınızı, 
                jargonlarınızı analiz ederek varlığınızı daha doğal hale getirir.
              </p>
            </div>

            {/* Platform Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Platform Seçin
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {chatPlatforms.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => setChatPlatform(platform.id)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      chatPlatform === platform.id
                        ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <div className="font-semibold text-slate-900 dark:text-white mb-1">
                      {platform.name}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      {platform.description}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                      {platform.fileFormats?.join(', ')}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Chat Dosyası Yükle
              </label>
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  chatFile 
                    ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' 
                    : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'
                }`}
              >
                {chatFile ? (
                  <div>
                    <div className="text-4xl mb-2">💬</div>
                    <div className="font-medium text-slate-900 dark:text-white mb-1">
                      {chatFile.name}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      {(chatFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    <button
                      onClick={handleClearChatImport}
                      className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Dosyayı Kaldır
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl mb-2">📁</div>
                    <div className="text-slate-600 dark:text-slate-400 mb-3">
                      {chatPlatform === 'whatsapp' ? 'WhatsApp .txt dosyanızı buraya sürükleyin' : 'Telegram .json dosyanızı buraya sürükleyin'}
                    </div>
                    <label className="inline-block px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 cursor-pointer transition-colors">
                      Dosya Seç
                      <input
                        type="file"
                        accept={chatPlatform === 'whatsapp' ? '.txt' : '.json,.zip'}
                        onChange={handleChatFileSelect}
                        className="hidden"
                      />
                    </label>
                    <div className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                      Maksimum dosya boyutu: 100MB
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            {chatImporting && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mb-2">
                  <span>İşleniyor...</span>
                  <span>{chatProgress}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${chatProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Import Button */}
            <button
              onClick={handleChatImport}
              disabled={!chatFile || chatImporting}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                !chatFile || chatImporting
                  ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white hover:from-cyan-700 hover:to-purple-700'
              }`}
            >
              {chatImporting ? 'Analiz Ediliyor...' : '🚀 Chat Geçmişini Analiz Et'}
            </button>

            {/* Import Results */}
            {chatResult && chatResult.success && (
              <div className="mt-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">✅</span>
                  <div className="flex-1">
                    <h4 className="font-bold text-green-800 dark:text-green-300 mb-3">
                      Analiz Tamamlandı!
                    </h4>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {chatResult.totalMessages}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Toplam Mesaj
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                          {chatResult.userMessages}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Sizin Mesajınız
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {chatResult.memoriesCreated}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Hafıza
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                          {chatResult.qaExamplesCreated}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Q&A Örneği
                        </div>
                      </div>
                    </div>

                    {chatResult.analysis && (
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          📊 Konuşma Stili Analizi:
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                          {chatResult.analysis.statistics && (
                            <div className="grid grid-cols-2 gap-2">
                              <div>Ortalama mesaj uzunluğu: {Math.round(chatResult.analysis.statistics.avg_message_length)} karakter</div>
                              <div>Emoji kullanımı: {chatResult.analysis.statistics.emoji_count} adet</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* How to Export Instructions */}
            <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-3">
                💡 Chat Geçmişinizi Nasıl Dışa Aktarırsınız?
              </h4>
              <div className="space-y-3 text-sm text-blue-800 dark:text-blue-400">
                {chatPlatforms.map((platform) => (
                  <div key={platform.id}>
                    <strong>{platform.name}:</strong> {platform.exportInstructions || 'Sohbet > Diğer > Sohbeti Dışa Aktar'}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Voice Notes Tab */}
      {activeTab === 'voice-notes' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-4 lg:p-6 border border-slate-200 dark:border-slate-700">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-slate-900 dark:text-white mb-2">
                🎤 Sesli Not İşleme
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Sesli notlarınızı yükleyin. Whisper ile otomatik transcript oluşturulur, 
                GPT-4 içeriği analiz eder ve hafızalar ile Q&A örnekleri oluşturulur.
              </p>
            </div>

            {/* Language Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Dil Seçin
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setVoiceLanguage('tr')}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    voiceLanguage === 'tr'
                      ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  🇹🇷 Türkçe
                </button>
                <button
                  onClick={() => setVoiceLanguage('en')}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    voiceLanguage === 'en'
                      ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  🇬🇧 English
                </button>
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Ses Dosyası Yükle
              </label>
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  voiceFile 
                    ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' 
                    : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'
                }`}
              >
                {voiceFile ? (
                  <div>
                    <div className="text-4xl mb-2">🎤</div>
                    <div className="font-medium text-slate-900 dark:text-white mb-1">
                      {voiceFile.name}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      {(voiceFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    <button
                      onClick={handleClearVoice}
                      className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Dosyayı Kaldır
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl mb-2">🎙️</div>
                    <div className="text-slate-600 dark:text-slate-400 mb-3">
                      Ses dosyanızı buraya sürükleyin
                    </div>
                    <label className="inline-block px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 cursor-pointer transition-colors">
                      Dosya Seç
                      <input
                        type="file"
                        accept=".mp3,.wav,.m4a,.webm,.ogg"
                        onChange={handleVoiceFileSelect}
                        className="hidden"
                      />
                    </label>
                    <div className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                      MP3, WAV, M4A, WebM, OGG • Maksimum 25MB
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            {voiceProcessing && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mb-2">
                  <span>İşleniyor... (Transcript + Analiz)</span>
                  <span>{voiceProgress}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${voiceProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Process Button */}
            <button
              onClick={handleVoiceProcess}
              disabled={!voiceFile || voiceProcessing}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                !voiceFile || voiceProcessing
                  ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white hover:from-cyan-700 hover:to-purple-700'
              }`}
            >
              {voiceProcessing ? 'Analiz Ediliyor...' : '🚀 Sesli Notu İşle'}
            </button>

            {/* Results */}
            {voiceResult && voiceResult.success && (
              <div className="mt-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">✅</span>
                  <div className="flex-1">
                    <h4 className="font-bold text-green-800 dark:text-green-300 mb-3">
                      İşlem Tamamlandı!
                    </h4>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {voiceResult.transcriptLength}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Karakter
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                          {voiceResult.memoriesCreated}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Hafıza
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {voiceResult.qaCreated}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Q&A
                        </div>
                      </div>
                    </div>

                    {/* Transcript */}
                    {voiceResult.transcript && (
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          📝 Transcript:
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400 max-h-40 overflow-y-auto">
                          {voiceResult.transcript}
                        </div>
                      </div>
                    )}

                    {/* Voice Characteristics */}
                    {voiceResult.voiceCharacteristics && (
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          🎙️ Konuşma Özellikleri:
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400 grid grid-cols-2 gap-2">
                          <div>Kelime sayısı: {voiceResult.voiceCharacteristics.word_count}</div>
                          <div>Cümle sayısı: {voiceResult.voiceCharacteristics.sentence_count}</div>
                          {voiceResult.voiceCharacteristics.uses_filler_words && (
                            <div className="col-span-2">Dolgu kelimeler kullanıyor (um, uh, yani, işte)</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Info Panel */}
            <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-3">
                💡 Nasıl Kullanılır?
              </h4>
              <div className="space-y-2 text-sm text-blue-800 dark:text-blue-400">
                <div>✓ Telefon veya bilgisayar mikrofonuyla ses kaydı yapın</div>
                <div>✓ MP3, WAV, M4A, WebM veya OGG formatında kaydedin</div>
                <div>✓ Dosyayı buradan yükleyin</div>
                <div>✓ Whisper otomatik olarak metne dönüştürür</div>
                <div>✓ GPT-4 içeriği analiz ederek hafızalar ve Q&A oluşturur</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
